import { Suspense, useEffect, type ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Layout } from './components/Layout';
import { RoleGuard } from './components/RoleGuard';
import { ErrorBoundary } from './components/ErrorBoundary';
import { SupabaseConfigError } from './components/ConnectionBanner';
import { supabaseConfigError } from './lib/supabase';
import { lazyRetry } from './lib/lazyRetry';
import { useAuthStore } from './stores/authStore';
import { PRIVILEGED_ROLES } from './types';

const Login = lazyRetry(() => import('./pages/Login/Login'), 'Login');
const AuthCallback = lazyRetry(() => import('./pages/AuthCallback/AuthCallback'), 'AuthCallback');
const Dashboard = lazyRetry(() => import('./pages/Dashboard/Dashboard'), 'Dashboard');
const Queue = lazyRetry(() => import('./pages/Queue/Queue'), 'Queue');
const CallHistory = lazyRetry(() => import('./pages/CallHistory/CallHistory'), 'CallHistory');
const Voicemails = lazyRetry(() => import('./pages/Voicemails/Voicemails'), 'Voicemails');

const Admin = lazyRetry(() => import('./pages/Admin/Admin'), 'Admin');
const AdminUsers = lazyRetry(() => import('./pages/Admin/Users'), 'AdminUsers');
const AdminPhonePool = lazyRetry(() => import('./pages/Admin/PhonePool'), 'AdminPhonePool');
const AdminBlacklist = lazyRetry(() => import('./pages/Admin/Blacklist'), 'AdminBlacklist');
const AdminTemplates = lazyRetry(() => import('./pages/Admin/Templates'), 'AdminTemplates');
const AdminProtocols = lazyRetry(() => import('./pages/Admin/Protocols'), 'AdminProtocols');
const AdminLegacyUsers = lazyRetry(() => import('./pages/Admin/LegacyUsers'), 'AdminLegacyUsers');
const AdminAuditLog = lazyRetry(() => import('./pages/Admin/AuditLog'), 'AdminAuditLog');
const AdminGlobalTestMode = lazyRetry(() => import('./pages/Admin/GlobalTestMode'), 'AdminGlobalTestMode');

const Insights = lazyRetry(() => import('./pages/Insights/Insights'), 'Insights');
const InsightsFunnel = lazyRetry(() => import('./pages/Insights/Funnel'), 'InsightsFunnel');
const InsightsStatistik = lazyRetry(() => import('./pages/Insights/Statistik'), 'InsightsStatistik');

const Team = lazyRetry(() => import('./pages/Team/Team'), 'Team');
const TeamAllCalls = lazyRetry(() => import('./pages/Team/AllCalls'), 'TeamAllCalls');
const TeamLeaderboard = lazyRetry(() => import('./pages/Team/Leaderboard'), 'TeamLeaderboard');

const Customers = lazyRetry(() => import('./pages/Customers/Customers'), 'Customers');
const CustomerDetail = lazyRetry(() => import('./pages/Customers/CustomerDetail'), 'CustomerDetail');
const Leads = lazyRetry(() => import('./pages/Leads/Leads'), 'Leads');
const AbandonedBookings = lazyRetry(() => import('./pages/AbandonedBookings/AbandonedBookings'), 'AbandonedBookings');
const Campaign = lazyRetry(() => import('./pages/Campaign/Campaign'), 'Campaign');
const Callbacks = lazyRetry(() => import('./pages/Callbacks/Callbacks'), 'Callbacks');
const BackOnTrack = lazyRetry(() => import('./pages/BackOnTrack/BackOnTrack'), 'BackOnTrack');
const Settings = lazyRetry(() => import('./pages/Settings/Settings'), 'Settings');
const Availability = lazyRetry(() => import('./pages/Availability/Availability'), 'Availability');
const Schedule = lazyRetry(() => import('./pages/Schedule/Schedule'), 'Schedule');
// Direktlink ohne Menüpunkt: Buchungsübersicht Wilmersdorf-Sommerferien 2026.
const FerienBuchungen = lazyRetry(() => import('./pages/FerienBuchungen/FerienBuchungen'), 'FerienBuchungen');

function Protected({ children }: { children: ReactNode }) {
  const { user, isInitialized } = useAuthStore();
  if (!isInitialized) return <div className="app-loading">Lade…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function Public({ children }: { children: ReactNode }) {
  const { user, isInitialized } = useAuthStore();
  if (!isInitialized) return <div className="app-loading">Lade…</div>;
  if (user) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export default function App() {
  const initialize = useAuthStore((s) => s.initialize);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // No usable CRM connection baked into the build → show a clear setup notice
  // instead of an empty page or an endless login loop.
  if (supabaseConfigError) {
    return <SupabaseConfigError reason={supabaseConfigError} />;
  }

  return (
    <ErrorBoundary>
    <Suspense fallback={<div className="app-loading">Lade…</div>}>
      <Routes>
        <Route path="/login" element={<Public><Login /></Public>} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route
          path="/"
          element={
            <Protected>
              <Layout />
            </Protected>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="queue" element={<Queue />} />
          <Route path="history" element={<CallHistory />} />
          <Route path="voicemails" element={<Voicemails />} />

          <Route path="customers" element={<Customers />} />
          <Route path="customers/:id" element={<CustomerDetail />} />
          <Route path="leads" element={<Leads />} />
          <Route path="abandoned-bookings" element={<AbandonedBookings />} />
          <Route path="campaign" element={<Campaign />} />
          <Route path="callbacks" element={<Callbacks />} />
          <Route path="back-on-track" element={<BackOnTrack />} />
          <Route path="availability" element={<Availability />} />
          <Route path="schedule" element={<Schedule />} />
          <Route path="wilmersdorf-sommer" element={<FerienBuchungen />} />
          <Route path="settings" element={<Settings />} />

          {/* Skript-/Leitfaden-Texte als eigener Menüpunkt unter „Verwaltung".
              Vorher nur als Unterpunkt in /admin versteckt und ausschließlich
              für Admins — Sharina hat ihn dort schlicht nicht gefunden. */}
          <Route
            path="skripte"
            element={
              <RoleGuard allow={PRIVILEGED_ROLES}>
                <AdminProtocols />
              </RoleGuard>
            }
          />

          <Route
            path="insights"
            element={
              <RoleGuard allow={['admin', 'supervisor', 'studioleiter']}>
                <Insights />
              </RoleGuard>
            }
          >
            <Route index element={<Navigate to="/insights/funnel" replace />} />
            <Route path="funnel" element={<InsightsFunnel />} />
            {/* Enger als die übrigen Auswertungen: die Statistik vergleicht
                namentlich einzelne Kolleginnen. Nur Admin — nicht Studioleitung,
                nicht die Servicekräfte. Die Datenbank prüft dasselbe noch einmal. */}
            <Route
              path="statistik"
              element={
                <RoleGuard allow={['admin']}>
                  <InsightsStatistik />
                </RoleGuard>
              }
            />
          </Route>

          <Route
            path="team"
            element={
              <RoleGuard allow={PRIVILEGED_ROLES}>
                <Team />
              </RoleGuard>
            }
          >
            <Route index element={<Navigate to="/team/calls" replace />} />
            <Route path="calls" element={<TeamAllCalls />} />
            <Route path="leaderboard" element={<TeamLeaderboard />} />
          </Route>

          <Route
            path="admin"
            element={
              <RoleGuard allow={['admin']}>
                <Admin />
              </RoleGuard>
            }
          >
            <Route index element={<Navigate to="/admin/users" replace />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="phone-pool" element={<AdminPhonePool />} />
            <Route path="blacklist" element={<AdminBlacklist />} />
            <Route path="templates" element={<AdminTemplates />} />
            <Route path="protocols" element={<AdminProtocols />} />
            <Route path="legacy-users" element={<AdminLegacyUsers />} />
            <Route path="audit-log" element={<AdminAuditLog />} />
            <Route path="test-mode" element={<AdminGlobalTestMode />} />
          </Route>
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
    </ErrorBoundary>
  );
}
